import { Link } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { Navbar, Footer, GlassCard } from '../components/Layout';
import EmailSignupForm from '../components/growth/EmailSignupForm';
import InsightsTeaser from '../components/growth/InsightsTeaser';
import ProductShowcase from '../components/growth/ProductShowcase';
import HowItWorks from '../components/growth/HowItWorks';
import HeroFuturePath from '../components/growth/HeroFuturePath';
import IntelligenceLayer from '../components/growth/IntelligenceLayer';
import SupportBot from '../components/SupportBot';
import { 
  GraduationCap, Briefcase, DollarSign, Target, 
  BookOpen, TrendingUp, Shield, ArrowRight, Zap,
  CheckCircle, Users, FileText, Star, Quote, Rocket,
  Brain, Award, Heart, Calendar, Sparkles, MapPin,
  Compass, Lightbulb, Trophy, Play, ChevronRight,
  BarChart3, Calculator, Bell, Lock, Wrench, Building,
  PiggyBank, FileCheck, Clock, RefreshCw, Eye
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

// Continuous counter component
function ContinuousCountUp({ baseValue, incrementPerSecond = 0.5, suffix = '' }) {
  const [count, setCount] = useState(baseValue);
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;
    const interval = setInterval(() => {
      setCount(prev => prev + incrementPerSecond);
    }, 1000);
    return () => clearInterval(interval);
  }, [isVisible, incrementPerSecond]);

  return <span ref={ref}>{Math.floor(count).toLocaleString()}{suffix}</span>;
}

// Animated counter
function CountUp({ end, suffix = '', duration = 2000 }) {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;
    let startTime;
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      setCount(Math.floor(easeOutQuart * end));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [isVisible, end, duration]);

  return <span ref={ref}>{count.toLocaleString()}{suffix}</span>;
}

// School logos
const schools = [
  { name: 'Stanford', logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/4/4b/Stanford_Cardinal_logo.svg/200px-Stanford_Cardinal_logo.svg.png' },
  { name: 'MIT', logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/0/0c/MIT_logo.svg/200px-MIT_logo.svg.png' },
  { name: 'Duke', logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/0/04/Duke_Blue_Devils_logo.svg/200px-Duke_Blue_Devils_logo.svg.png' },
  { name: 'UCLA', logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/0/0d/UCLA_Bruins_primary_logo.svg/200px-UCLA_Bruins_primary_logo.svg.png' },
  { name: 'Georgia Tech', logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/6/6c/Georgia_Tech_Yellow_Jackets_logo.svg/200px-Georgia_Tech_Yellow_Jackets_logo.svg.png' },
  { name: 'Michigan', logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/f/fb/Michigan_Wolverines_logo.svg/200px-Michigan_Wolverines_logo.svg.png' },
];

// Platform features with flip card functionality
const platformFeatures = [
  {
    id: 'pathways',
    icon: Compass,
    title: 'Every Career Pathway',
    description: 'College, skilled trades, military, entrepreneurship, healthcare and direct workforce, all mapped side by side',
    details: [
      'AI-powered pathway matching quiz',
      'Detailed comparison tools',
      'ROI calculators for each path',
      'Success metrics and outcomes'
    ],
    color: 'blue',
    screenshot: '/screenshots/pathways.png'
  },
  {
    id: 'financial',
    icon: DollarSign,
    title: 'Financial Intelligence',
    description: 'Real cost breakdowns, scholarship matching, and funding gap analysis',
    details: [
      'True cost vs sticker price',
      'Scholarship matching engine',
      '529 & savings projections',
      'Loan calculator & ROI'
    ],
    color: 'emerald',
    screenshot: '/screenshots/financial.png'
  },
  {
    id: 'family',
    icon: Users,
    title: 'Family Dashboard',
    description: 'Parents monitor progress, action items, and graduation countdowns',
    details: [
      'Child summary cards',
      'Financial overview',
      'Action items & to-dos',
      'Upcoming events calendar'
    ],
    color: 'purple',
    screenshot: '/screenshots/family.png'
  },
  {
    id: 'ai-advisor',
    icon: Sparkles,
    title: 'AI Parent Advisor',
    description: 'Premium AI-powered recommendations personalized to your family',
    details: [
      'Personalized action plans',
      'What\'s missing analysis',
      'Financial optimization',
      'Deadline tracking'
    ],
    color: 'pink',
    premium: true,
    screenshot: '/screenshots/ai-advisor.png'
  },
  {
    id: 'applications',
    icon: FileCheck,
    title: 'Application Pipeline',
    description: 'Track applications, deadlines, documents, and requirements',
    details: [
      'Application tracker',
      'Deadline engine',
      'Document vault',
      'Checklist generator'
    ],
    color: 'amber',
    screenshot: '/screenshots/applications.png'
  },
  {
    id: 'military',
    icon: Shield,
    title: 'Military Planning',
    description: 'MOS matching, GI Bill calculator, and branch comparisons',
    details: [
      'ASVAB requirements',
      'GI Bill benefits',
      'Branch comparison',
      'Career transition paths'
    ],
    color: 'green',
    screenshot: '/screenshots/military.png'
  },
  {
    id: 'trades',
    icon: Wrench,
    title: 'Trade Pipelines',
    description: 'Apprenticeships, union info, licensing requirements',
    details: [
      'Apprenticeship database',
      'Union membership info',
      'Licensing by state',
      'ROI vs college'
    ],
    color: 'orange',
    screenshot: '/screenshots/trades.png'
  },
  {
    id: 'notifications',
    icon: Bell,
    title: 'Smart Notifications',
    description: 'Email & SMS alerts for deadlines, changes, and recommendations',
    details: [
      'Deadline reminders',
      'Financial updates',
      'Academic progress',
      'Weekly digest emails'
    ],
    color: 'cyan',
    screenshot: '/screenshots/notifications.png'
  }
];

// Color mapping
const colorMap = {
  blue: 'bg-blue-500/20 border-blue-500/30 text-blue-400',
  emerald: 'bg-emerald-500/20 border-emerald-500/30 text-emerald-400',
  purple: 'bg-purple-500/20 border-purple-500/30 text-purple-400',
  pink: 'bg-pink-500/20 border-pink-500/30 text-pink-400',
  amber: 'bg-amber-500/20 border-amber-500/30 text-amber-400',
  green: 'bg-green-500/20 border-green-500/30 text-green-400',
  orange: 'bg-orange-500/20 border-orange-500/30 text-orange-400',
  cyan: 'bg-cyan-500/20 border-cyan-500/30 text-cyan-400',
};

// Testimonials
const testimonials = [
  { name: 'Sarah M.', state: 'California', role: 'Parent', feedback: "The financial intelligence dashboard changed everything. We found $45,000 in scholarships!", rating: 5 },
  { name: 'Marcus T.', state: 'Texas', role: 'Student', feedback: "I was going to skip college, but FuturePath showed me trade careers that pay more. Now I'm in an IBEW apprenticeship.", rating: 5 },
  { name: 'Patricia W.', state: 'Georgia', role: 'Parent', feedback: "As a single mom, understanding college costs was overwhelming. The AI advisor gave me a clear action plan.", rating: 5 },
  { name: 'Carlos R.', state: 'Florida', role: 'Parent', feedback: "My twins had completely different paths - engineering and culinary arts. FuturePath guided both perfectly.", rating: 5 },
  { name: 'Aisha J.', state: 'New York', role: 'Student', feedback: "The military pathway tools helped me understand GI Bill benefits. I'm now planning to serve and get my degree.", rating: 5 },
  { name: 'James W.', state: 'Michigan', role: 'Parent', feedback: "In this AI age, I was worried about my son's career choices. FuturePath showed us future-proof paths.", rating: 5 }
];

// Career path icons
const pathIcons = [
  { icon: GraduationCap, label: 'College', color: 'blue' },
  { icon: Wrench, label: 'Trades', color: 'emerald' },
  { icon: Rocket, label: 'Startup', color: 'purple' },
  { icon: Shield, label: 'Military', color: 'amber' },
  { icon: Briefcase, label: 'Workforce', color: 'cyan' },
  { icon: Heart, label: 'Healthcare', color: 'rose' },
];

// Mini Preview Components for Flip Cards
const PathwaysPreview = () => (
  <div className="h-full p-3 space-y-2">
    <div className="flex justify-between items-center mb-2">
      <span className="text-xs text-slate-300">Career Pathway Quiz</span>
      <span className="text-xs text-emerald-400">92% Match</span>
    </div>
    {[
      { icon: GraduationCap, name: 'College', match: 92, color: 'blue' },
      { icon: Wrench, name: 'Trade', match: 78, color: 'amber' },
      { icon: Shield, name: 'Military', match: 65, color: 'green' },
    ].map((path, i) => (
      <div key={i} className="flex items-center gap-2 p-2 bg-slate-800/50 rounded-lg">
        <path.icon className={`w-4 h-4 text-${path.color}-400`} />
        <span className="text-xs text-white flex-1">{path.name}</span>
        <div className="w-16 h-1.5 bg-slate-700 rounded-full overflow-hidden">
          <div className={`h-full bg-${path.color}-500`} style={{ width: `${path.match}%` }} />
        </div>
        <span className="text-xs text-slate-300">{path.match}%</span>
      </div>
    ))}
  </div>
);

const FinancialPreview = () => (
  <div className="h-full p-3 space-y-2">
    <div className="grid grid-cols-2 gap-2 mb-2">
      <div className="p-2 bg-blue-500/10 rounded-lg text-center">
        <p className="text-lg font-bold text-blue-400">$186K</p>
        <p className="text-[10px] text-slate-300">Total Cost</p>
      </div>
      <div className="p-2 bg-emerald-500/10 rounded-lg text-center">
        <p className="text-lg font-bold text-emerald-400">$45K</p>
        <p className="text-[10px] text-slate-300">Saved</p>
      </div>
    </div>
    <div className="p-2 bg-amber-500/10 rounded-lg">
      <div className="flex justify-between text-xs mb-1">
        <span className="text-slate-300">Funding Gap</span>
        <span className="text-amber-400">$141K</span>
      </div>
      <div className="h-1.5 bg-slate-700 rounded-full overflow-hidden">
        <div className="h-full bg-emerald-500" style={{ width: '24%' }} />
      </div>
    </div>
    <div className="text-[10px] text-slate-300 text-center">3 Scholarships Found</div>
  </div>
);

const FamilyPreview = () => (
  <div className="h-full p-3 space-y-2">
    <div className="flex items-center gap-2 p-2 bg-slate-800/50 rounded-lg">
      <div className="w-8 h-8 rounded-lg bg-blue-500 flex items-center justify-center text-white text-xs font-bold">R</div>
      <div className="flex-1">
        <p className="text-xs text-white">Riley - Grade 11</p>
        <p className="text-[10px] text-slate-300">Duke University</p>
      </div>
      <div className="text-right">
        <p className="text-xs text-purple-400 font-bold">440</p>
        <p className="text-[10px] text-slate-300">days</p>
      </div>
    </div>
    <div className="flex items-center gap-2 p-2 bg-slate-800/50 rounded-lg">
      <div className="w-8 h-8 rounded-lg bg-amber-500 flex items-center justify-center text-white text-xs font-bold">A</div>
      <div className="flex-1">
        <p className="text-xs text-white">Alex - Grade 10</p>
        <p className="text-[10px] text-slate-300">Trade - IBEW</p>
      </div>
      <div className="text-right">
        <p className="text-xs text-purple-400 font-bold">805</p>
        <p className="text-[10px] text-slate-300">days</p>
      </div>
    </div>
    <div className="grid grid-cols-2 gap-2">
      <div className="p-1.5 bg-emerald-500/10 rounded text-center">
        <p className="text-xs text-emerald-400">$59K</p>
        <p className="text-[10px] text-slate-300">Saved</p>
      </div>
      <div className="p-1.5 bg-amber-500/10 rounded text-center">
        <p className="text-xs text-amber-400">$347K</p>
        <p className="text-[10px] text-slate-300">Gap</p>
      </div>
    </div>
  </div>
);

const AIAdvisorPreview = () => (
  <div className="h-full p-3 space-y-2">
    <div className="flex items-center gap-2 mb-2">
      <Sparkles className="w-4 h-4 text-purple-400" />
      <span className="text-xs text-white">AI Recommendations</span>
    </div>
    {[
      { text: 'Complete FAFSA by March 1', priority: 'high' },
      { text: 'Schedule Duke campus tour', priority: 'medium' },
      { text: 'Review scholarship list', priority: 'low' },
    ].map((item, i) => (
      <div key={i} className={`p-2 rounded-lg border ${
        item.priority === 'high' ? 'bg-red-500/10 border-red-500/20' :
        item.priority === 'medium' ? 'bg-amber-500/10 border-amber-500/20' :
        'bg-blue-500/10 border-blue-500/20'
      }`}>
        <p className="text-[10px] text-white">{item.text}</p>
      </div>
    ))}
    <div className="p-2 bg-purple-500/10 rounded-lg">
      <p className="text-[10px] text-purple-300 italic">"You're on track! Focus on scholarships..."</p>
    </div>
  </div>
);

const ApplicationsPreview = () => (
  <div className="h-full p-3 space-y-2">
    <div className="flex justify-between text-xs mb-1">
      <span className="text-slate-300">Applications</span>
      <span className="text-blue-400">4 Active</span>
    </div>
    {[
      { name: 'Duke University', status: 'In Progress', color: 'amber' },
      { name: 'Georgia Tech', status: 'Submitted', color: 'emerald' },
      { name: 'MIT', status: 'Not Started', color: 'slate' },
    ].map((app, i) => (
      <div key={i} className="flex items-center justify-between p-2 bg-slate-800/50 rounded-lg">
        <span className="text-xs text-white">{app.name}</span>
        <span className={`text-[10px] text-${app.color}-400`}>{app.status}</span>
      </div>
    ))}
    <div className="p-2 bg-red-500/10 rounded-lg border border-red-500/20">
      <p className="text-[10px] text-red-400">⚠️ Duke deadline: 14 days</p>
    </div>
  </div>
);

const MilitaryPreview = () => (
  <div className="h-full p-3 space-y-2">
    <div className="flex justify-between text-xs mb-1">
      <span className="text-slate-300">Branch Comparison</span>
    </div>
    {[
      { branch: 'Army', mos: '35F Intel', bonus: '$40K' },
      { branch: 'Navy', mos: 'IT', bonus: '$25K' },
      { branch: 'Air Force', mos: 'Cyber', bonus: '$35K' },
    ].map((b, i) => (
      <div key={i} className="flex items-center justify-between p-2 bg-slate-800/50 rounded-lg">
        <div>
          <p className="text-xs text-white">{b.branch}</p>
          <p className="text-[10px] text-slate-300">{b.mos}</p>
        </div>
        <span className="text-xs text-emerald-400">{b.bonus}</span>
      </div>
    ))}
    <div className="p-2 bg-green-500/10 rounded-lg text-center">
      <p className="text-xs text-green-400">GI Bill: $100K+ Value</p>
    </div>
  </div>
);

const TradesPreview = () => (
  <div className="h-full p-3 space-y-2">
    <div className="flex justify-between text-xs mb-1">
      <span className="text-slate-300">Apprenticeships</span>
      <span className="text-amber-400">12 Near You</span>
    </div>
    {[
      { trade: 'IBEW Electrician', salary: '$85K', time: '4 yrs' },
      { trade: 'UA Plumber', salary: '$78K', time: '5 yrs' },
      { trade: 'HVAC Tech', salary: '$65K', time: '2 yrs' },
    ].map((t, i) => (
      <div key={i} className="flex items-center justify-between p-2 bg-slate-800/50 rounded-lg">
        <div>
          <p className="text-xs text-white">{t.trade}</p>
          <p className="text-[10px] text-slate-300">{t.time}</p>
        </div>
        <span className="text-xs text-emerald-400">{t.salary}</span>
      </div>
    ))}
    <div className="p-2 bg-amber-500/10 rounded-lg text-center">
      <p className="text-[10px] text-amber-400">Earn while you learn - No debt!</p>
    </div>
  </div>
);

const NotificationsPreview = () => (
  <div className="h-full p-3 space-y-2">
    <div className="flex justify-between text-xs mb-1">
      <span className="text-slate-300">Recent Alerts</span>
      <span className="text-cyan-400">3 New</span>
    </div>
    {[
      { text: 'FAFSA deadline in 14 days', type: 'deadline', time: '2h ago' },
      { text: 'New scholarship match found', type: 'financial', time: '5h ago' },
      { text: 'Riley completed SAT prep', type: 'academic', time: '1d ago' },
    ].map((n, i) => (
      <div key={i} className="flex items-start gap-2 p-2 bg-slate-800/50 rounded-lg">
        <div className={`w-2 h-2 rounded-full mt-1 ${
          n.type === 'deadline' ? 'bg-red-400' :
          n.type === 'financial' ? 'bg-emerald-400' : 'bg-blue-400'
        }`} />
        <div className="flex-1">
          <p className="text-[10px] text-white">{n.text}</p>
          <p className="text-[10px] text-slate-300">{n.time}</p>
        </div>
      </div>
    ))}
  </div>
);

// Preview component mapping
const previewComponents = {
  pathways: PathwaysPreview,
  financial: FinancialPreview,
  family: FamilyPreview,
  'ai-advisor': AIAdvisorPreview,
  applications: ApplicationsPreview,
  military: MilitaryPreview,
  trades: TradesPreview,
  notifications: NotificationsPreview,
};

// Flip Card Component
const FlipCard = ({ feature }) => {
  const [isFlipped, setIsFlipped] = useState(false);
  const Icon = feature.icon;
  const PreviewComponent = previewComponents[feature.id];
  
  return (
    <div 
      className="relative h-[320px] perspective-1000 cursor-pointer"
      onClick={() => setIsFlipped(!isFlipped)}
    >
      <motion.div
        className="relative w-full h-full"
        initial={false}
        animate={{ rotateY: isFlipped ? 180 : 0 }}
        transition={{ duration: 0.6 }}
        style={{ transformStyle: 'preserve-3d' }}
      >
        {/* Front */}
        <div 
          className={`absolute inset-0 rounded-2xl p-6 border bg-slate-800/50 border-slate-700 hover:border-${feature.color}-500/50 transition-colors backface-hidden`}
          style={{ backfaceVisibility: 'hidden' }}
        >
          <div className={`w-14 h-14 rounded-xl ${colorMap[feature.color]} flex items-center justify-center mb-4`}>
            <Icon className="w-7 h-7" />
          </div>
          
          <h3 className="text-xl font-semibold text-white mb-2 flex items-center gap-2">
            {feature.title}
            {feature.premium && (
              <span className="px-2 py-0.5 text-xs bg-gradient-to-r from-purple-500 to-pink-500 rounded-full text-white">
                Premium
              </span>
            )}
          </h3>
          
          <p className="text-slate-300 text-sm mb-4">{feature.description}</p>
          
          <ul className="space-y-2">
            {feature.details.map((detail, idx) => (
              <li key={idx} className="flex items-center gap-2 text-sm text-slate-300">
                <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                {detail}
              </li>
            ))}
          </ul>
          
          <div className="absolute bottom-4 right-4 text-slate-300 text-xs flex items-center gap-1">
            <Eye className="w-3 h-3" /> Click to preview
          </div>
        </div>
        
        {/* Back - Mini Dashboard Preview */}
        <div 
          className="absolute inset-0 rounded-2xl border bg-slate-900 border-slate-700 overflow-hidden backface-hidden"
          style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}
        >
          <div className="h-full flex flex-col">
            <div className="flex items-center justify-between p-3 border-b border-cyan-300/10">
              <h4 className="text-white font-medium text-sm flex items-center gap-2">
                <Icon className={`w-4 h-4 text-${feature.color}-400`} />
                {feature.title}
              </h4>
              <span className="text-slate-300 text-xs">Click to flip</span>
            </div>
            
            {/* Mini Dashboard Preview */}
            <div className="flex-1 overflow-hidden">
              {PreviewComponent && <PreviewComponent />}
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// Stats Section
const statsData = [
  { value: 31, suffix: '', label: 'Life situations the engine reads', icon: Compass },
  { value: 20, suffix: '', label: 'Archetypes it configures itself into', icon: Users },
  { value: 19, suffix: '', label: 'Sections in your FuturePath report', icon: FileText },
  { value: 4500, suffix: '+', label: 'Colleges & programs indexed', icon: GraduationCap },
];

export default function HomePage() {
  const [activeSection, setActiveSection] = useState('hero');
  
  // Track active section on scroll
  useEffect(() => {
    const sections = ['hero', 'problem', 'inside', 'how', 'intelligence', 'pathways', 'parents', 'testimonials'];
    const handleScroll = () => {
      for (const id of sections.reverse()) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top <= 120) {
          setActiveSection(id);
          break;
        }
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const tabs = [
    { id: 'hero', label: 'Home' },
    { id: 'problem', label: 'Why Agentic FuturePath' },
    { id: 'inside', label: 'Inside' },
    { id: 'how', label: 'How it works' },
    { id: 'intelligence', label: 'Intelligence' },
    { id: 'pathways', label: 'Pathways' },
    { id: 'parents', label: 'For Parents' },
    { id: 'testimonials', label: 'Reviews' },
  ];

  return (
    <div className="min-h-screen bg-aurora">
      <Navbar sectionTabs={tabs} activeSection={activeSection} onTabClick={scrollTo} />
      
      {/* ═══════════════ HERO SECTION ═══════════════ */}
      {/* ═══════════════ HERO, the Living FuturePath ═══════════════ */}
      <HeroFuturePath />

      {/* ═══════════════ PRODUCT SHOWCASE, real app screens ═══════════════ */}
      <ProductShowcase />

      {/* ═══════════════ HOW IT WORKS, one question, the journey, PIE ═══════════════ */}
      <HowItWorks />

      {/* ═══════════════ PROBLEM STATEMENT - MOVED UP ═══════════════ */}
      <section id="problem" className="py-24 md:py-32 px-6 md:px-12 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-blue-950/20 to-transparent" />
        <div className="max-w-7xl mx-auto relative">
          <div className="text-center mb-16">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <span className="text-xs font-semibold tracking-[0.2em] uppercase text-cyan-400 mb-4 block">The Problem</span>
              <h2 className="font-outfit text-3xl sm:text-4xl lg:text-5xl font-semibold text-white mb-6">
                Career guidance stops exactly where the hard part starts
              </h2>
              <p className="text-lg text-slate-300/90 max-w-2xl mx-auto leading-relaxed">
                Three failures, and every one of them costs people years and money they cannot get back.
              </p>
            </motion.div>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Clock, color: 'cyan', title: 'The window is too narrow',
                body: 'Almost everything built for this decision serves a four-year window around one choice at eighteen. Then it goes quiet, through the first pivot, the layoff, the credential decision at thirty, the industry shift at forty-five.',
              },
              {
                icon: Calculator, color: 'emerald', title: 'The advice is not priced',
                body: 'You get told to follow your passion, and nobody works out what the life you want actually costs. So the salary needed to live it is never the thing the recommendation is measured against.',
              },
              {
                icon: Target, color: 'amber', title: 'The answer stops at the noun',
                body: 'It names an occupation and stops. Not which of your skills already transfer, not the four that are missing, not the credential that closes the gap, not who pays for it, not how long it takes.',
              },
            ].map((item, index) => (
              <motion.div key={index} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.15 }}>
                <GlassCard className="h-full text-left">
                  <item.icon className={`w-8 h-8 text-${item.color}-400 mb-5`} />
                  <h3 className="font-outfit text-xl font-semibold text-white mb-3">{item.title}</h3>
                  <p className="text-slate-300/85 leading-relaxed">{item.body}</p>
                </GlassCard>
              </motion.div>
            ))}
          </div>

          {/* Urgency callout */}
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.5 }}
            className="mt-12 text-center">
            <p className="text-lg text-slate-300 font-medium">
              85 million jobs shift by 2030 and 97 million new roles appear <span className="text-slate-300">(World Economic Forum)</span>.
              The question isn't <em className="text-white">if</em> your path is affected, it's <em className="text-cyan-400">how prepared you are when it is</em>.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ═══════════════ INTELLIGENCE LAYER ═══════════════ */}
      <IntelligenceLayer />

      {/* ═══════════════ INTERACTIVE PATHWAYS ═══════════════ */}
      <section id="pathways" className="py-20 px-6 md:px-12 relative">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-outfit text-3xl sm:text-4xl font-semibold text-white mb-4">
              Every Path Forward, Mapped
            </h2>
            <p className="text-lg text-slate-300/90 max-w-2xl mx-auto">Not everyone needs a four-year degree. We help you find the right path, whatever that looks like, at whatever age you're deciding.</p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 lg:gap-6">
            {pathIcons.map((path, index) => {
              const Icon = path.icon;
              return (
                <motion.div key={path.label} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }}
                  whileHover={{ scale: 1.08, y: -8 }}
                  className={`relative glass-tint ${['glass-cyan','glass-violet','glass-amber','glass-emerald'][index % 4]} p-6 cursor-pointer group overflow-hidden`}>
                  <div className={`absolute inset-0 bg-gradient-to-br from-${path.color}-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />
                  <div className={`relative w-16 h-16 rounded-xl bg-${path.color}-500/20 flex items-center justify-center mb-4 mx-auto group-hover:scale-110 group-hover:bg-${path.color}-500/30 transition-all duration-300`}>
                    <Icon className={`w-8 h-8 text-${path.color}-400 group-hover:text-${path.color}-300 transition-colors`} />
                  </div>
                  <p className="relative text-white font-semibold text-center">{path.label}</p>
                  <div className={`absolute bottom-0 left-1/2 -translate-x-1/2 w-3/4 h-1 bg-gradient-to-r from-transparent via-${path.color}-500 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══════════════ STATS STRIP ═══════════════ */}
      <section className="relative py-20 px-6 md:px-12 bg-gradient-to-b from-[#050d20]/80 via-transparent to-transparent">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 lg:gap-8">
            {statsData.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <motion.div key={index} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }}
                  className={`text-center glass-tint ${['glass-cyan','glass-violet','glass-emerald','glass-amber'][index % 4]} p-8`}>
                  <Icon className="w-8 h-8 text-cyan-200 mx-auto mb-3" />
                  <p className="text-3xl md:text-4xl font-bold text-white mb-1">{stat.prefix}<CountUp end={stat.value} suffix={stat.suffix} /></p>
                  <p className="text-slate-300/80 text-sm">{stat.label}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══════════════ SCHOOL LOGOS ═══════════════ */}
      <section className="relative py-8 overflow-hidden border-y border-cyan-300/10 bg-[#040a1a]/50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 mb-4">
          <p className="text-center text-slate-300 text-xs uppercase tracking-wider">Guiding students and professionals to their next career milestone</p>
        </div>
        <div className="relative">
          <div className="flex school-scroll gap-10 items-center">
            {[...schools, ...schools, ...schools].map((school, index) => (
              <div key={index} className="flex-shrink-0 flex items-center gap-2 px-4 py-2 bg-slate-800/30 rounded-full border border-slate-700/50">
                <img src={school.logo} alt={school.name} className="w-6 h-6 object-contain" onError={(e) => { e.target.style.display = 'none' }} />
                <span className="font-outfit text-sm font-medium text-slate-300 whitespace-nowrap">{school.name}</span>
              </div>
            ))}
          </div>
        </div>
        <style>{`
          @keyframes schoolScroll { 0% { transform: translateX(0); } 100% { transform: translateX(-33.33%); } }
          .school-scroll { animation: schoolScroll 25s linear infinite; width: fit-content; }
          .school-scroll:hover { animation-play-state: paused; }
        `}</style>
      </section>

      {/* ═══════════════ PARENT SECTION ═══════════════ */}
      <section id="parents" className="py-24 md:py-32 px-6 md:px-12 bg-gradient-to-b from-purple-900/10 to-transparent">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <span className="text-xs font-semibold tracking-[0.2em] uppercase text-purple-400 mb-4 block">For Parents</span>
              <h2 className="font-outfit text-3xl sm:text-4xl lg:text-5xl font-semibold text-white mb-6">
                Your Family's Education Command Center
              </h2>
              <p className="text-lg text-slate-300/90 mb-10 leading-relaxed">
                Stop guessing. Start guiding. Monitor every child's journey, track financial progress, 
                and get AI-powered recommendations, all from one powerful dashboard.
              </p>
              
              <div className="space-y-5 mb-10">
                {[
                  { icon: Calendar, text: 'Graduation countdown timers for each child' },
                  { icon: DollarSign, text: 'Real-time financial gap tracking & scholarships' },
                  { icon: Bell, text: 'Email & SMS deadline notifications' },
                  { icon: Sparkles, text: 'AI Parent Advisor with personalized action plans' },
                  { icon: Shield, text: 'COPPA & FERPA compliant, your data is safe' },
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-4">
                    <div className="w-11 h-11 rounded-lg bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-5 h-5 text-purple-400" />
                    </div>
                    <span className="text-slate-300 text-base">{item.text}</span>
                  </div>
                ))}
              </div>
              
              <div className="flex flex-wrap gap-3">
                <Link to="/pricing" className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl text-white font-semibold hover:from-purple-700 hover:to-pink-700 transition-all text-lg" data-testid="parent-cta">
                  Start your free trial <ArrowRight className="w-5 h-5" />
                </Link>
                <Link to="/financial-preview" className="inline-flex items-center gap-2 px-8 py-4 border-2 border-slate-600 rounded-xl text-white font-semibold hover:border-purple-500/50 hover:bg-purple-500/10 transition-all text-lg" data-testid="financial-preview-cta">
                  See Financial Dashboard
                </Link>
              </div>
            </motion.div>
            
            <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="relative">
              <div className="rounded-2xl bg-gradient-to-br from-purple-900/50 to-pink-900/50 border border-purple-500/20 p-8">
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-blue-500 flex items-center justify-center text-white font-bold">R</div>
                      <div>
                        <p className="text-white font-medium">Riley - Grade 11</p>
                        <p className="text-slate-300 text-sm">College Bound - Duke University</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-purple-400 font-bold">440 days</p>
                      <p className="text-slate-300 text-xs">to graduation</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-emerald-500/10 rounded-xl border border-emerald-500/20">
                      <p className="text-emerald-400 text-2xl font-bold">$59,371</p>
                      <p className="text-slate-300 text-sm">Saved</p>
                    </div>
                    <div className="p-4 bg-amber-500/10 rounded-xl border border-amber-500/20">
                      <p className="text-amber-400 text-2xl font-bold">$347,278</p>
                      <p className="text-slate-300 text-sm">Gap</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══════════════ TESTIMONIALS ═══════════════ */}
      <section id="testimonials" className="py-24 md:py-32 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-xs font-semibold tracking-[0.2em] uppercase text-cyan-400 mb-4 block">Testimonials</span>
            <h2 className="font-outfit text-3xl sm:text-4xl lg:text-5xl font-semibold text-white mb-4">
              Families Love FuturePath
            </h2>
            <p className="text-lg text-slate-300/90">Real stories from parents and students across the country</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div key={index} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }}>
                <GlassCard className="h-full">
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 text-amber-400 fill-amber-400" />
                    ))}
                  </div>
                  <Quote className="w-8 h-8 text-blue-500/30 mb-2" />
                  <p className="text-slate-300 mb-6 italic leading-relaxed">"{testimonial.feedback}"</p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold">{testimonial.name.charAt(0)}</div>
                    <div>
                      <p className="text-white font-medium">{testimonial.name}</p>
                      <p className="text-slate-300 text-sm">{testimonial.role} &bull; {testimonial.state}</p>
                    </div>
                  </div>
                </GlassCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════ FINAL CTA ═══════════════ */}
      <section className="py-24 md:py-32 px-6 md:px-12">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="font-outfit text-3xl sm:text-4xl lg:text-5xl font-semibold text-white mb-6">
              Don't Leave the Next Decade to Chance
            </h2>
            <p className="text-lg text-slate-300/90 mb-10 max-w-2xl mx-auto leading-relaxed">
              Students, career changers, professionals and the families supporting them, all making sharper
              education and career decisions on one platform. Plans start at $22/month, cancel anytime.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/pricing" className="btn-primary inline-flex items-center justify-center gap-2 text-lg px-10 py-5 rounded-xl" data-testid="final-cta-primary">
                Create My Account <ArrowRight className="w-5 h-5" />
              </Link>
              <Link to="/pricing" className="btn-secondary inline-flex items-center justify-center gap-2 text-lg px-10 py-5 rounded-xl" data-testid="final-cta-secondary">
                Compare Plans
              </Link>
            </div>
            
            <p className="text-slate-300 text-sm mt-8">
              No long-term contract &bull; Cancel anytime
            </p>
          </motion.div>
        </div>
      </section>

      <InsightsTeaser />

      <section className="max-w-4xl mx-auto px-4 sm:px-6 pb-20">
        <EmailSignupForm variant="full" source="homepage_cta"
          heading="Not ready to sign up? Join the waitlist."
          sub="We'll email you early-access news, new guides and invitations to demos, pilots and workshops." />
      </section>
      <Footer />
      <SupportBot />
    </div>
  );
}
